import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import Footer from './Footer';
import useSEO from '../hooks/useSEO';

// LMS environments - simplified
const lmsEnvironments = [
  {
    id: 'canvas',
    name: 'Canvas',
    tagline: 'The Modern Standard',
    description: 'Seamless Grade Passback and intuitive course integration.',
    studentUrl: 'https://canvas-demo.veydra.io/enroll/C6EX6J',
    instructorUrl: 'https://canvas-demo.veydra.io/enroll/C6EX6J',
  },
  {
    id: 'moodle',
    name: 'Moodle',
    tagline: 'The Technical/Global Choice',
    description: 'Open-source flexibility with deep customization options.',
    studentUrl: 'https://moodle-demo.veydra.io/',
    instructorUrl: 'https://moodle-demo.veydra.io/',
    credentials: {
      instructor: { username: 'demo_instructor', password: 'VeydraDemo1!' },
      student: { username: 'demo_student', password: 'VeydraDemo1!' },
    },
  },
  {
    id: 'oli-torus',
    name: 'OLI Torus',
    tagline: 'The Research Lab',
    description: 'Advanced Learning Engineering with adaptive analytics.',
    studentUrl: 'https://torus-demo.veydra.io/',
    instructorUrl: 'https://torus-demo.veydra.io/',
  },
];

const DemoPage = ({ logo }) => {
  useSEO({
    title: 'Try Veydra – LMS Demo Environments',
    description: 'Try Veydra integrated with Canvas, Moodle, and OLI Torus. Explore simulation-driven learning in your LMS.'
  });

  return (
    <div className="min-h-screen bg-white">
      <TopNav logo={logo} />

      {/* Hero */}
      <main className="max-w-4xl mx-auto px-8 py-16 md:py-24 text-center">
        <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
          Try It Now
        </p>
        <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
          Veydra + Your LMS
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
          See how Veydra integrates with leading learning management systems via LTI 1.3. 
          Live demo environments — try them now.
        </p>
      </main>

      {/* LMS Integrations */}
      <section className="py-16 md:py-20 px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-4 tracking-tight text-center">
            Choose Your LMS
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-xl mx-auto">
            Launch a live demo environment and see Veydra in action within your preferred platform.
          </p>

          {/* Single column list */}
          <div className="space-y-4">
            {lmsEnvironments.map((lms) => (
              <div 
                key={lms.id}
                className="bg-white border border-gray-200 rounded-lg p-5 hover:border-gray-300 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2">
                      <h3 className="text-lg font-medium text-gray-900">{lms.name}</h3>
                      <span className="text-sm text-gray-500">— {lms.tagline}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{lms.description}</p>
                    {lms.credentials && (
                      <div className="mt-3 text-xs text-gray-500 bg-gray-50 rounded px-3 py-2">
                        <span className="font-medium">Demo Login:</span>{' '}
                        <code className="bg-gray-100 px-1 rounded">{lms.credentials.instructor.username}</code> / <code className="bg-gray-100 px-1 rounded">{lms.credentials.instructor.password}</code>
                      </div>
                    )}
                  </div>
                  <a
                    href={lms.studentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 rounded bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap"
                  >
                    Launch {lms.name} Now →
                  </a>
                </div>
              </div>
            ))}

            {/* Other LMS option */}
            <div 
              className="bg-white border border-gray-200 rounded-lg p-5 hover:border-gray-300 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-lg font-medium text-gray-900">Other LMS</h3>
                    <span className="text-sm text-gray-500">— Custom Integration</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Using Blackboard, D2L Brightspace, or another platform? We can integrate via LTI 1.3.</p>
                </div>
                <Link
                  to="/contact"
                  className="px-6 py-3 rounded border border-gray-300 text-gray-700 text-sm font-medium hover:border-gray-400 hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  Let's Chat →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 px-8 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-6 tracking-tight">
            Ready to bring this to your classroom?
          </h2>
          <p className="text-lg text-gray-300 mb-10 leading-relaxed max-w-2xl mx-auto">
            We'll walk you through the platform, discuss your courses, and help you get started with a pilot.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-white text-gray-900 px-8 py-4 rounded text-base font-medium hover:bg-gray-100 transition-colors min-w-[220px]">
                Schedule a Demo
              </button>
            </Link>
            <Link to="/educators">
              <button className="border border-gray-600 text-white px-8 py-4 rounded text-base font-medium hover:border-gray-400 transition-colors min-w-[220px]">
                Learn More for Educators
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DemoPage;